import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiStar } from 'react-icons/fi';

const ACTIVITIES = [
  {
    key: 'buggy',
    image: '/images/buggy.JPG',
    badgeJa: '大人気 No.1',
    badgeEn: 'Most Popular',
    id: 'buggy-section',
    emoji: '🏎️',
    color: 'from-[#ff7b37] to-[#d94d16]',
  },
  {
    key: 'chainsaw',
    image: '/images/chainsaw.png',
    badgeJa: 'ユニーク体験',
    badgeEn: 'Unique Experience',
    emoji: '🪚',
    color: 'from-[#7b4d24] to-[#44220f]',
  },
  {
    key: 'naan',
    image: '/images/naan.png',
    badgeJa: '本格体験',
    badgeEn: 'Authentic',
    emoji: '🫓',
    color: 'from-[#e5a43b] to-[#c96d1d]',
  },
  {
    key: 'vegetable',
    image: '/images/vegetable.png',
    badgeJa: '自然体験',
    badgeEn: 'Nature Experience',
    emoji: '🥕',
    color: 'from-[#4b8d3d] to-[#215322]',
  },
  {
    key: 'trampoline',
    image: '/images/trampoline.png',
    badgeJa: '全年齢OK',
    badgeEn: 'For Everyone',
    emoji: '🦘',
    color: 'from-[#3c7044] to-[#18351d]',
  },
  {
    key: 'restaurant',
    image: '/images/restaurant.png',
    badgeJa: 'レストラン',
    badgeEn: 'Restaurant',
    emoji: '🍛',
    color: 'from-[#8a4422] to-[#4d1d0f]',
  },
];

export default function Activities() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="activities"
      className="relative overflow-hidden bg-[#f7f3ea] py-24 px-5"
    >

      {/* BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-transparent" />
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-[#dfead8]/40 to-transparent" />
      <div className="absolute bottom-0 right-0 text-[220px] opacity-[0.03] pointer-events-none">
        🌲
      </div>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="
            inline-flex items-center gap-3
            bg-white border border-[#eadfcb]
            rounded-full px-7 py-3
            shadow-lg mb-6
          ">
            <FiStar className="text-[#d89b22] text-xl" />
            <span className="text-[#8c6225] font-black tracking-wide">
              {lang === 'ja' ? 'アウトドア体験' : 'Outdoor Experiences'}
            </span>
          </div>

          <h2 className="text-[#173617] text-5xl md:text-7xl font-black leading-tight mb-5">
            {lang === 'ja' ? '体験メニュー' : 'Experience Menu'}
          </h2>

          <p className="text-[#5d5d5d] text-lg md:text-2xl max-w-3xl mx-auto leading-relaxed">
            {lang === 'ja'
              ? '自然の中で楽しめる特別なアウトドア体験をお楽しみください。'
              : 'Enjoy exciting outdoor experiences surrounded by nature.'}
          </p>
        </motion.div>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {ACTIVITIES.map((item, index) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              onClick={() => item.id && scrollTo(item.id)}
              className="
                group relative overflow-hidden
                rounded-[34px] bg-white
                border border-[#ece2d0]
                shadow-[0_10px_35px_rgba(0,0,0,0.08)]
                cursor-pointer
              "
            >
              {/* IMAGE */}
              <div className="relative h-[320px] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.key}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />

                {/* OVERLAY */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                {/* BADGE */}
                <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-md rounded-full px-5 py-2 shadow-lg">
                  <span className="text-[#173617] text-sm font-black">
                    {lang === 'ja' ? item.badgeJa : item.badgeEn}
                  </span>
                </div>

                {/* EMOJI */}
                <div className={`
                  absolute bottom-5 right-5
                  w-20 h-20 rounded-full
                  bg-gradient-to-br ${item.color}
                  border border-white/20
                  shadow-2xl
                  flex items-center justify-center
                  text-4xl
                `}>
                  {item.emoji}
                </div>

                {/* TITLE OVER IMAGE */}
                <div className="absolute left-7 bottom-6 right-28">
                  <h3 className="text-white text-4xl font-black leading-tight drop-shadow-lg">
                    {t(`activities.${item.key}.name`)}
                  </h3>
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-8">
                <p className="text-[#555] text-lg leading-relaxed">
                  {t(`activities.${item.key}.desc`)}
                </p>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}